/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { Configuration, LogLevel, RedirectRequest } from '@azure/msal-browser';
import * as log from 'loglevel';

import { authConfig } from './config';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

export const msalConfig: Configuration = {
  auth: {
    clientId: authConfig.clientId || '', // This is the ONLY mandatory field that you need to supply.
    authority: authConfig.authority || '', // Replace the placeholder with your tenant subdomain
    redirectUri: authConfig.redirectUri || '', // Points to window.location.origin. You must register this URI on Microsoft Entra admin center/App Registration.
    postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            log.error(message);
            return;
          case LogLevel.Info:
            log.info(message);
            return;
          case LogLevel.Verbose:
            log.debug(message);
            return;
          case LogLevel.Warning:
            log.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

const apiClientId = authConfig.apiClientId;

export const scopes = [
  `api://${apiClientId}/rtg-oim.AppSettings.Read`,
  `api://${apiClientId}/rtg-oim.TwilioAccounts.Read`,
  `api://${apiClientId}/rtg-oim.Targets.Read`,
  `api://${apiClientId}/rtg-oim.Scopes.Read`,
  `api://${apiClientId}/rtg-oim.AppSettings.ReadWrite`,
  `api://${apiClientId}/rtg-oim.TwilioAccounts.ReadWrite`,
  `api://${apiClientId}/rtg-oim.Targets.ReadWrite`,
  `api://${apiClientId}/rtg-oim.Scopes.ReadWrite`,
];

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest: RedirectRequest = {
  scopes,
};
